import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Home, RotateCcw, HelpCircle } from 'lucide-react';
import confetti from 'canvas-confetti';
import axios from 'axios';

const INDIA_MAP_BG = 'https://images.unsplash.com/photo-1546833998-07256bcc76ad?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxJbmRpYSUyMG1hcCUyMGNvbG9yZnVsJTIwaWxsdXN0cmF0aW9ufGVufDB8fHx8MTc3NzA0NDIxMXww&ixlib=rb-4.1.0&q=85';
const STAR_URL =
  'https://static.prod-images.emergentagent.com/jobs/0e7ec82b-be21-4167-ae09-299c58207254/images/51c2f4d391b08733a1282c5c803c88045bd7e3e5993310b66fbe2ae387825c7c.png';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WhoAmIResultsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const savedRef = useRef(false);

  const score = parseInt(searchParams.get('score') || '0', 10);
  const total = parseInt(searchParams.get('total') || '10', 10);
  const percentage = total > 0 ? Math.round((score / total) * 100) : 0;
  const stars = percentage >= 90 ? 3 : percentage >= 70 ? 2 : percentage >= 50 ? 1 : 0;

  useEffect(() => {
    if (!savedRef.current) {
      savedRef.current = true;
      axios.post(`${API}/whoami/results`, { player_name: localStorage.getItem('gaurik_player_name') || 'Player', score, total_questions: total }).catch(console.error);
    }
    if (percentage >= 50) {
      const end = Date.now() + 2500;
      const frame = () => {
        confetti({ particleCount: 4, angle: 60, spread: 55, origin: { x: 0 } });
        confetti({ particleCount: 4, angle: 120, spread: 55, origin: { x: 1 } });
        if (Date.now() < end) requestAnimationFrame(frame);
      };
      frame();
    }
  }, [percentage, score, total]);

  const getMessage = () => {
    if (percentage >= 90) return 'ANIMAL EXPERT!';
    if (percentage >= 70) return 'GREAT JOB!';
    if (percentage >= 50) return 'WELL DONE!';
    return 'KEEP TRYING!';
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center p-6"
      style={{ backgroundImage: `url(${INDIA_MAP_BG})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed' }}
    >
      <div className="glass-panel p-8 max-w-md w-full text-center">
        <HelpCircle className="w-12 h-12 mx-auto mb-3 text-amber-400" />

        <h1 data-testid="wai-results-message" className="font-heading text-3xl sm:text-4xl font-black text-amber-950 mb-1">
          {getMessage()}
        </h1>
        <p className="font-body text-sm text-amber-800 mb-5">
          {percentage >= 70 ? 'You really know your animals!' : 'Every game makes you smarter!'}
        </p>

        <div className="flex justify-center gap-3 mb-5" data-testid="wai-results-stars">
          {[1, 2, 3].map((i) => (
            <img
              key={i}
              src={STAR_URL}
              alt={`star ${i}`}
              className={`w-14 h-14 animate-star-in ${i <= stars ? '' : 'grayscale opacity-30'}`}
              style={{ animationDelay: `${i * 250}ms` }}
            />
          ))}
        </div>

        <p data-testid="wai-results-score" className="font-heading text-5xl font-black text-amber-950 mb-1">
          {score}/{total}
        </p>
        <p className="font-body text-base font-bold text-amber-800 mb-6">{percentage}% correct</p>

        <div className="flex flex-col gap-3">
          <button
            data-testid="wai-play-again"
            onClick={() => navigate('/who-am-i/play')}
            className="bg-orange-500 text-white font-black text-lg py-4 px-8 rounded-full border-b-[6px] border-orange-700 active:border-b-0 active:translate-y-[6px] transition-all hover:bg-orange-400 flex items-center justify-center gap-2 shadow-lg"
          >
            <RotateCcw className="w-6 h-6" />
            Play Again
          </button>
          <button
            data-testid="wai-go-home"
            onClick={() => navigate('/')}
            className="bg-emerald-500 text-white font-black text-lg py-4 px-8 rounded-full border-b-[6px] border-emerald-700 active:border-b-0 active:translate-y-[6px] transition-all hover:bg-emerald-400 flex items-center justify-center gap-2 shadow-lg"
          >
            <Home className="w-6 h-6" />
            Back to Studio
          </button>
        </div>
      </div>
    </div>
  );
}
